import React, { useEffect, useRef, useState } from 'react';
import './TradingViewChart.css';
import Datafeed from './datafeed';
import websocketService from '../../services/websocket';

const TradingViewChart = ({ market, containerId = 'tv_chart_container' }) => {
  const chartContainerRef = useRef(null);
  const tvWidgetRef = useRef(null);
  const datafeedRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [currentApiInterval, setCurrentApiInterval] = useState('1m');
  const [currentTvInterval, setCurrentTvInterval] = useState('1');
  const currentRequestIdRef = useRef(null);
  const [currentTheme, setCurrentTheme] = useState(
    document.body.classList.contains('theme-light') ? 'light' : 'dark'
  );

  // Function to determine current theme
  const getCurrentTheme = () => {
    return document.body.classList.contains('theme-light') ? 'light' : 'dark';
  };

  // Function to handle theme changes
  const handleThemeChange = () => {
    const newTheme = getCurrentTheme();
    setCurrentTheme(newTheme);
    
    // Update chart theme if widget exists
    if (tvWidgetRef.current) {
      tvWidgetRef.current.onChartReady(() => {
        try {
          // Apply new theme to existing chart
          tvWidgetRef.current.changeTheme(newTheme);
        } catch (err) {
          console.error('Error changing chart theme:', err);
        }
      });
    }
  };
  
  // Listen for theme changes
  useEffect(() => {
    // Create a mutation observer to watch for class changes on the body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });
    
    // Start observing the body for class changes
    observer.observe(document.body, { attributes: true });
    
    // Clean up observer on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  // Function to handle graph data response
  const handleGraphData = (response) => {
    // Check if this response matches our current request
    if (response && response.request_id && response.request_id !== currentRequestIdRef.current) {
      return;
    }
    
    if (response && response.type === 'chartDataRes' && Array.isArray(response.data)) {
      setGraphData(response.data);
      setIsDataLoading(false);
      
      // Get interval from the first data point if available
      if (response.data.length > 0 && response.data[0].interval) {
        const responseInterval = response.data[0].interval;
        
        // Update the chart's interval if needed
        if (responseInterval !== currentApiInterval && tvWidgetRef.current) {
          // Wait for the chart to be ready
          tvWidgetRef.current.onChartReady(() => {
            const chart = tvWidgetRef.current.chart();
            // Convert API interval to TV interval
            const tvInterval = convertApiIntervalToTv(responseInterval);
            
            if (tvInterval && tvInterval !== currentTvInterval) {
              chart.setResolution(tvInterval, () => {
                setCurrentTvInterval(tvInterval);
                setCurrentApiInterval(responseInterval);
              });
            }
          });
        }
      }
    }
  };

  // Convert API interval format to TradingView format
  const convertApiIntervalToTv = (apiInterval) => {
    switch(apiInterval) {
      case '1m': return '1';
      case '5m': return '5';
      case '15m': return '15';
      case '30m': return '30';
      case '1h': return '60';
      case '4h': return '240';
      case '1d': return 'D';
      case '1w': return 'W';
      case '1M': return 'M';
      default: return null;
    }
  };

  // Convert TradingView interval format to API format
  const convertTvIntervalToApi = (tvInterval) => {
    // Handle numeric intervals (minutes)
    if (tvInterval === '1') return '1m';
    if (tvInterval === '5') return '5m';
    if (tvInterval === '15') return '15m';
    if (tvInterval === '30') return '30m';
    if (tvInterval === '60') return '1h';
    if (tvInterval === '240') return '4h';
    // Handle days, weeks, months
    if (tvInterval === 'D' || tvInterval === '1D') return '1d';
    if (tvInterval === 'W' || tvInterval === '1W') return '1w';
    if (tvInterval === 'M' || tvInterval === '1M') return '1M';
    return null;
  };

  // Function to calculate appropriate time range based on the interval in seconds
  const calculateTimeRange = (interval) => {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    let startTime;
    
    // Determine appropriate time range based on interval
    switch (interval) {
      case '1m':
        // For 1-minute candles, fetch last 12 hours (720 candles)
        startTime = now - (12 * 60 * 60);
        break;
      case '5m':
        // For 5-minute candles, fetch last 2 days (576 candles)
        startTime = now - (2 * 24 * 60 * 60);
        break;
      case '15m':
        // For 15-minute candles, fetch last 5 days (480 candles)
        startTime = now - (5 * 24 * 60 * 60);
        break;
      case '30m':
        // For 30-minute candles, fetch last 10 days (480 candles)
        startTime = now - (10 * 24 * 60 * 60);
        break;
      case '1h':
        // For 1-hour candles, fetch last 20 days (480 candles)
        startTime = now - (20 * 24 * 60 * 60);
        break;
      case '4h':
        // For 4-hour candles, fetch last 60 days (360 candles)
        startTime = now - (60 * 24 * 60 * 60);
        break;
      case '1d':
        // For daily candles, fetch last 365 days
        startTime = now - (365 * 24 * 60 * 60);
        break;
      case '1w':
        // For weekly candles, fetch last 3 years
        startTime = now - (3 * 365 * 24 * 60 * 60);
        break;
      case '1M':
        // For monthly candles, fetch last 5 years
        startTime = now - (5 * 365 * 24 * 60 * 60);
        break;
      default:
        // Default to 7 days if interval not recognized
        startTime = now - (7 * 24 * 60 * 60);
    }
    
    return { startTime, endTime: now };
  };

  // Function to fetch graph data with specific interval
  const fetchGraphData = (symbol, interval) => {
    if (!symbol) return;
    
    // Validate interval format to prevent errors
    const validIntervals = ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1M'];
    
    if (!validIntervals.includes(interval)) {
      console.error(`Invalid interval value: ${interval}. Must be one of: ${validIntervals.join(', ')}`);
      return;
    }

    // Show loading indicator when fetching new data
    setIsDataLoading(true);
    
    // Clear previous data
    setGraphData(null);
    
    // Store the current API interval
    setCurrentApiInterval(interval);
    
    // Generate dynamic request ID
    const requestId = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    
    // Save current request ID to filter responses
    currentRequestIdRef.current = requestId;
    
    // Calculate appropriate time range based on the selected interval
    const { startTime, endTime } = calculateTimeRange(interval);
    
    // Create the request with dynamic values
    const graphRequest = {
      type: 'get-instruments-graph',
      request_id: requestId,
      data: {
        symbol: symbol,
        startTime: startTime,
        endTime: endTime,
        interval: interval
      }
    };
    
    // Clean up any previous listener
    websocketService.off('chartDataRes', handleGraphData);
    
    // Set up listener for graph response
    websocketService.on('chartDataRes', handleGraphData);
    
    // Send request
    websocketService.send(graphRequest);
  };

  // Fetch graph data when market changes
  useEffect(() => {
    // Reset state when market changes
    setGraphData(null);
    setIsDataLoading(true);
    
    // Clear current request ID
    currentRequestIdRef.current = null;
    
    // Clear datafeed data
    if (datafeedRef.current) {
      datafeedRef.current.clearData();
    }
    
    if (market?.originalId) {
      // When market changes, fetch data with default interval
      // Add a small delay before fetching data to ensure proper initialization
      const timeoutId = setTimeout(() => {
        fetchGraphData(market.originalId, '1m');
      }, 300);
      
      // Clean up the timeout on unmount
      return () => {
        clearTimeout(timeoutId);
        websocketService.off('chartDataRes', handleGraphData);
      };
    }
    
    // Cleanup function
    return () => {
      websocketService.off('chartDataRes', handleGraphData);
    };
  }, [market]);

  useEffect(() => {
    // Load TradingView library
    const loadTradingViewLibrary = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Check if window is defined (browser environment)
        if (typeof window === 'undefined') return;

        // Check if TradingView is already loaded
        if (!window.TradingView) {
          // Use absolute path from public folder
          const tvScriptPath = `${process.env.PUBLIC_URL}/libs/tradingView/charting_library-master/charting_library/charting_library.standalone.js`;
          
          // Create and load script
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = tvScriptPath;
          script.async = true;
          
          // Create a promise to wait for script to load
          const scriptLoadPromise = new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = (error) => {
              console.error('Failed to load TradingView library:', error);
              reject(error);
            };
          });
          
          // Add script to document
          document.head.appendChild(script);
          
          // Wait for script to load
          await scriptLoadPromise;
        }
        
        // Initialize chart after library is loaded
        initializeChart();
      } catch (error) {
        console.error('Error loading TradingView library:', error);
        setError('Failed to load chart library. Please refresh the page.');
        setIsLoading(false);
      }
    };

    // Initialize the chart
    const initializeChart = () => {
      if (!window.TradingView) {
        console.error('TradingView library not loaded');
        setError('Chart library not available. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      // Clean up previous widget if it exists
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
      }

      // Extract decimals from market data if available
      let marketDecimals = null;
      if (market) {
        // Check for various possible decimal indicators in the backend data
        const decimals = market.decimals || 
                      market.pips || 
                      market.precision || 
                      market.tick_size || 
                      market.tickSize ||
                      (market.settings && market.settings.precision) ||
                      (market.settings && market.settings.decimals);
        
        // If we found explicit decimal information, use it
        if (typeof decimals === 'number') {
          marketDecimals = decimals;
        }
        // If decimals is a string that might represent a decimal value
        else if (typeof decimals === 'string') {
          // For formats like "0.00001" (tick size), count the decimal places
          if (decimals.match(/^0\.\d+$/)) {
            marketDecimals = decimals.split('.')[1].length;
          } else {
            // Try parsing as a number
            const parsedDecimals = parseInt(decimals, 10);
            if (!isNaN(parsedDecimals)) {
              marketDecimals = parsedDecimals;
            }
          }
        }
        
        // If still no valid decimals, try to determine from instrument type
        if (marketDecimals === null) {
          if (market.originalId) {
            // Forex pairs with JPY usually have 3 decimals
            if (market.originalId.includes('JPY')) {
              marketDecimals = 3;
            }
            // Standard forex pairs have 5 decimals
            else if (market.originalId.includes('/') || 
                    market.category === 'forex' ||
                    /USD|EUR|GBP|AUD|NZD|CAD|CHF/.test(market.originalId)) {
              marketDecimals = 5;
            }
            // Cryptocurrencies typically use 8 decimals
            else if (market.category === 'crypto' ||
                    /BTC|ETH|LTC|XRP|BCH|ADA|DOT|SOL/.test(market.originalId)) {
              marketDecimals = 8;
            }
          } else if (market.category === 'forex') {
            marketDecimals = 5;
          } else if (market.category === 'crypto' || market.category === 'cryptocurrency') {
            marketDecimals = 8;
          }
          
          // Last resort: Try to derive from price
          if (marketDecimals === null) {
            if (typeof market.price === 'number' || 
                typeof market.lastPrice === 'number' || 
                typeof market.currentPrice === 'number') {
              const price = market.price || market.lastPrice || market.currentPrice;
              const priceString = price.toString();
              const decimalPart = priceString.split('.')[1];
              
              if (decimalPart && decimalPart.length > 0) {
                marketDecimals = Math.max(decimalPart.length, 4);
              } else {
                marketDecimals = 4; // Default if no decimals in price
              }
            } else {
              marketDecimals = 4; // Default if no price available
            }
          }
        }
      }

      // If no decimals could be determined, use a default of 4
      if (marketDecimals === null) {
        marketDecimals = 4;
      }

      // Determine the symbol to use
      const symbol = market?.originalId || 'BTCUSD';
      
      // Create new datafeed instance with symbol and decimals information
      datafeedRef.current = new Datafeed(symbol, marketDecimals, market?.category || 'forex');

      // Configure widget
      const widgetOptions = {
        symbol: symbol,
        interval: currentTvInterval,  // Use tracked interval
        container: containerId,
        datafeed: datafeedRef.current,
        library_path: `${process.env.PUBLIC_URL}/libs/tradingView/charting_library-master/charting_library/`,
        locale: 'en',
        disabled_features: [
          'use_localstorage_for_settings',
          'header_symbol_search',
          'header_compare'
        ],
        enabled_features: [
          'study_templates',
          'side_toolbar_in_fullscreen_mode'
        ],
        timeframe: getTimeframe(currentTvInterval), // Dynamic timeframe based on interval
        time_frames: [
          { text: "1D", resolution: "1", description: "1 Day" },
          { text: "5D", resolution: "5", description: "5 Days" },
          { text: "1W", resolution: "15", description: "1 Week" },
          { text: "1M", resolution: "60", description: "1 Month" },
          { text: "3M", resolution: "D", description: "3 Months" },
          { text: "6M", resolution: "D", description: "6 Months" },
          { text: "1Y", resolution: "D", description: "1 Year" },
        ],
        supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D', 'W', 'M'],
        charts_storage_url: 'https://saveload.tradingview.com',
        charts_storage_api_version: '1.1',
        client_id: 'trading_platform_demo',
        user_id: 'public_user_id',
        fullscreen: false,
        autosize: true,
        theme: currentTheme, // Use the current theme
        overrides: {
          "mainSeriesProperties.style": 1, // Candles style
          "paneProperties.background": currentTheme === 'dark' ? "#131722" : "#ffffff",
          "paneProperties.vertGridProperties.color": currentTheme === 'dark' ? "#242424" : "#e1e1e1",
          "paneProperties.horzGridProperties.color": currentTheme === 'dark' ? "#242424" : "#e1e1e1",
        }
      };

      try {
        // Create widget
        tvWidgetRef.current = new window.TradingView.widget(widgetOptions);
        
        // Add widget events
        tvWidgetRef.current.onChartReady(() => {
          setIsLoading(false);
          
          // Get chart instance
          const chart = tvWidgetRef.current.chart();
          
          // Set initial visible range based on current interval
          try {
            const timeRange = getTimeRangeForInterval(currentTvInterval);
            
            // Set initial visible range
            setTimeout(() => {
              chart.setVisibleRange(timeRange, { animation: false });
            }, 500); // Slight delay to ensure data is loaded
          } catch (err) {
            console.error('Error setting initial visible range:', err);
          }
          
          // Subscribe to resolution change events
          chart.onIntervalChanged().subscribe(null, (tvInterval) => {
            // Store the current TV interval
            setCurrentTvInterval(tvInterval);
            
            // Convert to API interval format
            const apiInterval = convertTvIntervalToApi(tvInterval);
            
            // Only fetch if we have a valid interval mapping
            if (apiInterval && ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1M'].includes(apiInterval)) {
              // Clear chart data before fetching new data
              if (datafeedRef.current) {
                datafeedRef.current.clearData();
              }

              // Clear state data
              setGraphData(null);
              
              // Fetch new data with updated interval
              if (market?.originalId) {
                fetchGraphData(market.originalId, apiInterval);
              }
              
              // Set appropriate visible range for the selected interval
              try {
                const timeRange = getTimeRangeForInterval(tvInterval);
                chart.setVisibleRange(timeRange, { animation: true });
              } catch (err) {
                console.error('Error setting visible range:', err);
              }
            } else {
              console.warn(`Unsupported interval: ${tvInterval}. Skipping data fetch.`);
            }
          });
        });
      } catch (error) {
        console.error('Error initializing TradingView widget:', error);
        setError('Failed to initialize chart. Please try again later.');
        setIsLoading(false);
      }
    };

    loadTradingViewLibrary();

    // Clean up function
    return () => {
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
      }
    };
  }, [containerId, graphData, currentTvInterval, market]); // Add market to dependencies to reinitialize chart

  // Effect to update chart when market changes
  useEffect(() => {
    if (tvWidgetRef.current && market?.originalId) {
      // Wait for the chart to be ready before changing the symbol
      tvWidgetRef.current.onChartReady(() => {
        // Instead of just changing the symbol, we'll reinitialize the chart for a full reset
        // First, remove the existing widget
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
        
        // Create a new datafeed instance
        datafeedRef.current = new Datafeed(null, null, market?.category || 'forex');
        
        // Now fetch data for the new market
        if (market?.originalId) {
          fetchGraphData(market.originalId, currentApiInterval);
        }
      });
    }
  }, [market]);

  // Effect to update chart data when graphData changes
  useEffect(() => {
    if (datafeedRef.current && graphData) {
      // Pass the data to the datafeed regardless of the symbol format
      // The datafeed will handle the formatting and comparison internally
      datafeedRef.current.updateData(graphData);
    }
  }, [graphData, market]);

  // Ensure websocket connection is established
  useEffect(() => {
    // Check if websocket is connected
    const connectionState = websocketService.getConnectionState();
    
    if (connectionState !== 'connected') {
      websocketService.connect()
        .catch((error) => {
          console.error('Failed to connect to WebSocket:', error);
        });
    }
    
    // Clean up function
    return () => {
      // Do not disconnect websocket as other components might be using it
      // The websocketService handles reference counting internally
    };
  }, []);

  // Effect to handle real-time price updates
  useEffect(() => {
    if (!datafeedRef.current || !market?.originalId) {
      return;
    }

    // Create a handler for price feed events
    const handlePriceFeed = (data) => {
      try {
        if (!data || !data.symbol) {
          return;
        }

        // Normalize the symbols for comparison
        const marketId = market.originalId;
        const dataSymbol = data.symbol;

        // Check if the incoming price is for the currently displayed market
        // This uses several matching strategies to account for different symbol formats
        const exactMatch = marketId === dataSymbol;
        const marketContainsSymbol = marketId && marketId.includes(`.${dataSymbol}`);
        const symbolContainsMarket = marketId && dataSymbol.includes(marketId);
        
        // Handle multi-part symbols (e.g., 0000001.FX.AUDCAD)
        let instrumentMatch = false;
        if (dataSymbol && dataSymbol.includes('.') && marketId && marketId.includes('.')) {
          const dataSymbolParts = dataSymbol.split('.');
          const marketIdParts = marketId.split('.');
          
          if (dataSymbolParts.length > 2 && marketIdParts.length > 2) {
            instrumentMatch = dataSymbolParts[2] === marketIdParts[2];
          }
        }
        
        // Handle the case where one format uses dots and the other doesn't
        let simplifiedMatch = false;
        if (dataSymbol && dataSymbol.includes('.') && dataSymbol.split('.').length > 2) {
          if (marketId && !marketId.includes('.')) {
            simplifiedMatch = dataSymbol.split('.')[2] === marketId;
          }
        }
        
        const matchesSymbol = exactMatch || marketContainsSymbol || symbolContainsMarket || 
                             instrumentMatch || simplifiedMatch;

        if (matchesSymbol) {
          // Calculate mid price from bid and ask
          let price = null;
          if (data.bid !== null && data.ask !== null) {
            price = (parseFloat(data.bid) + parseFloat(data.ask)) / 2;
          } else if (data.bid !== null) {
            price = parseFloat(data.bid);
          } else if (data.ask !== null) {
            price = parseFloat(data.ask);
          }

          if (price !== null) {
            // Send price to the datafeed for real-time update
            datafeedRef.current.updateRealtimeBar(price);
          }
        }
      } catch (error) {
        console.error('Error handling price feed:', error);
      }
    };

    // Subscribe to price feed events
    websocketService.on('price-feed', handlePriceFeed);

    // Clean up subscription when component unmounts or market changes
    return () => {
      websocketService.off('price-feed', handlePriceFeed);
    };
  }, [market]);

  // Helper function to determine appropriate timeframe for chart based on interval
  const getTimeframe = (interval) => {
    // Map TradingView intervals to appropriate visible timeframes
    switch (interval) {
      case '1':   // 1 minute
        return '12H'; // Show 12 hours
      case '5':   // 5 minutes
        return '2D';  // Show 2 days
      case '15':  // 15 minutes
        return '5D';  // Show 5 days
      case '30':  // 30 minutes
        return '10D'; // Show 10 days
      case '60':  // 1 hour
        return '20D'; // Show 20 days
      case '240': // 4 hours
        return '60D'; // Show 60 days
      case 'D':   // 1 day
        return '365D'; // Show 1 year
      case 'W':   // 1 week
        return '156W'; // Show 3 years (156 weeks)
      case 'M':   // 1 month
        return '60M'; // Show 5 years (60 months)
      default:
        return '1D';  // Default to 1 day
    }
  };

  // Helper function to calculate time range in milliseconds based on interval for chart display
  const getTimeRangeForInterval = (interval) => {
    const now = new Date().getTime();
    let fromTimestamp;
    
    // Calculate from time based on interval
    switch (interval) {
      case '1':   // 1 minute
        fromTimestamp = now - (1 * 60 * 60 * 1000); // 1 hour instead of 3 hours
        break;
      case '5':   // 5 minutes
        fromTimestamp = now - (4 * 60 * 60 * 1000); // 4 hours instead of 12 hours
        break;
      case '15':  // 15 minutes
        fromTimestamp = now - (8 * 60 * 60 * 1000); // 8 hours instead of 1 day
        break;
      case '30':  // 30 minutes
        fromTimestamp = now - (16 * 60 * 60 * 1000); // 16 hours instead of 2 days
        break;
      case '60':  // 1 hour
        fromTimestamp = now - (40 * 60 * 60 * 1000); // 40 hours (~1.5 days) instead of 5 days
        break;
      case '240': // 4 hours
        fromTimestamp = now - (5 * 24 * 60 * 60 * 1000); // 5 days instead of 14 days
        break;
      case 'D':   // 1 day
        fromTimestamp = now - (20 * 24 * 60 * 60 * 1000); // 20 days instead of 60 days
        break;
      case 'W':   // 1 week
        fromTimestamp = now - (9 * 7 * 24 * 60 * 60 * 1000); // 9 weeks instead of 26 weeks
        break;
      case 'M':   // 1 month
        fromTimestamp = now - (4 * 30 * 24 * 60 * 60 * 1000); // 4 months instead of 12 months
        break;
      default:
        fromTimestamp = now - (1 * 24 * 60 * 60 * 1000); // Default to 1 day instead of 3 days
    }
    
    return { 
      from: fromTimestamp / 1000, // Convert to seconds for TradingView
      to: now / 1000              // Convert to seconds for TradingView
    };
  };

  return (
    <div className="tradingview-chart-wrapper">
      {(isLoading || isDataLoading) && (
        <div className="tradingview-chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading chart{isDataLoading ? ' data' : ''}...</p>
        </div>
      )}
      
      {error && (
        <div className="tradingview-chart-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      )}
      
      <div 
        id={containerId} 
        ref={chartContainerRef} 
        className={`tradingview-chart ${isLoading || isDataLoading ? 'loading' : ''}`} 
      />
    </div>
  );
};

export default TradingViewChart; 